import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getBills, payBill, payBillsByUnit, getBillSummary } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { CreditCard, IndianRupee, AlertCircle, CheckCircle2, Clock, Wallet, Receipt, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ResidentBills = () => {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({ total_amount: 0, total_paid: 0, total_pending: 0, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [unitNumber, setUnitNumber] = useState(user?.unit_number || '');
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const fetchBills = async () => {
    try {
      const data = await getBills();
      setBills(data);
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await getBillSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  useEffect(() => {
    fetchBills();
    fetchSummary();
  }, []);

  const handlePayBill = async (billId) => {
    try {
      setPaying(billId);
      await payBill(billId);
      toast.success('Bill paid successfully!');
      fetchBills();
      fetchSummary();
    } catch (error) {
      toast.error('Payment failed');
    } finally {
      setPaying(null);
    }
  };

  const handleMakePayment = async (isFullPayment = false) => {
    if (!unitNumber.trim()) {
      toast.error('Please enter your flat/house number');
      return;
    }
    
    const amount = isFullPayment ? summary.total_pending : parseFloat(paymentAmount);
    
    if (!isFullPayment && (!amount || amount <= 0)) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setPaymentProcessing(true);
      const result = await payBillsByUnit(unitNumber, amount);
      toast.success(result.message);
      setPaymentDialogOpen(false);
      setPaymentAmount('');
      fetchBills();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Paid</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Partial</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-1" /> Overdue</Badge>;
      default:
        return <Badge className="bg-orange-100 text-orange-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getBillTypeBadge = (type) => {
    switch (type) {
      case 'amenity':
        return <Badge variant="outline" className="text-blue-600 border-blue-200">Amenity</Badge>;
      case 'maintenance':
        return <Badge variant="outline" className="text-purple-600 border-purple-200">Maintenance</Badge>;
      default:
        return <Badge variant="outline">Other</Badge>;
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="resident-bills">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>My Bills</h1>
          <p className="text-sm md:text-base text-slate-600">View and pay your bills</p>
        </div>
        
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700" disabled={summary.total_pending <= 0} data-testid="make-payment-btn">
              <Wallet className="h-4 w-4 mr-2" />
              Make Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Make Payment
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500 mb-1">Total Pending Amount</p>
                <p className="text-3xl font-bold text-slate-900">₹{summary.total_pending.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">{summary.pending_count} pending bill(s)</p>
              </div>

              {/* Unit Number Input */}
              <div className="space-y-2">
                <Label htmlFor="unit_number">Flat/House Number <span className="text-red-500">*</span></Label>
                <Input
                  id="unit_number"
                  placeholder="Enter your flat/house number (e.g., A-101)"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  data-testid="payment-unit-input"
                />
              </div>

              {/* Full Payment */}
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => handleMakePayment(true)}
                disabled={paymentProcessing || summary.total_pending <= 0}
                data-testid="full-payment-btn"
              >
                {paymentProcessing ? 'Processing...' : `Pay Full Amount (₹${summary.total_pending.toFixed(2)})`}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or Part Payment</span>
                </div>
              </div>

              {/* Part Payment */}
              <div className="space-y-2">
                <Label htmlFor="part_amount">Payment Amount</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="part_amount"
                      type="number"
                      min="0"
                      max={summary.total_pending}
                      step="0.01"
                      placeholder="Enter amount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="pl-10"
                      data-testid="part-payment-amount-input"
                    />
                  </div>
                  <Button 
                    onClick={() => handleMakePayment(false)}
                    disabled={paymentProcessing || !paymentAmount}
                    data-testid="part-payment-btn"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Part payment will be applied to oldest bills first</p>
              </div>

              <p className="text-xs text-center text-slate-400">This is a mock payment for demonstration purposes</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Total Paid</p>
                <p className="text-xl md:text-2xl font-bold text-green-700">₹{summary.total_paid.toFixed(2)}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Total Pending</p>
                <p className="text-xl md:text-2xl font-bold text-orange-700">₹{summary.total_pending.toFixed(2)}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Total Billed</p>
                <p className="text-xl md:text-2xl font-bold text-slate-900">₹{summary.total_amount.toFixed(2)}</p>
              </div>
              <Receipt className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Pending Bills</p>
                <p className="text-xl md:text-2xl font-bold text-slate-900">{summary.pending_count}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bills List */}
      <div className="space-y-4">
        {bills.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <CreditCard className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No bills found</p>
            </CardContent>
          </Card>
        ) : (
          bills.map((bill) => {
            const remainingAmount = bill.amount - (bill.paid_amount || 0);
            
            return (
              <Card key={bill.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="bill-card">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IndianRupee className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900">{bill.description}</h3>
                          {getStatusBadge(bill.status)}
                          {getBillTypeBadge(bill.bill_type)}
                        </div>
                        <p className="text-sm text-slate-500">Due: {bill.due_date}</p>
                        {bill.status === 'partial' && (
                          <p className="text-sm text-yellow-600 mt-1">
                            Paid: ₹{(bill.paid_amount || 0).toFixed(2)} / Remaining: ₹{remainingAmount.toFixed(2)}
                          </p>
                        )}
                        {bill.paid_date && (
                          <p className="text-xs text-green-600 mt-1">Paid on: {bill.paid_date}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-slate-900">₹{bill.amount.toFixed(2)}</p>
                        {bill.status === 'partial' && (
                          <p className="text-sm text-slate-500">Remaining: ₹{remainingAmount.toFixed(2)}</p>
                        )}
                      </div>
                      {bill.status !== 'paid' && (
                        <Button
                          onClick={() => handlePayBill(bill.id)}
                          disabled={paying === bill.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`pay-bill-btn-${bill.id}`}
                        >
                          {paying === bill.id ? 'Paying...' : 'Pay Now'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ResidentBills;
